import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { Code } from '@chakra-ui/react';

const MCP_URL = 'https://mcp.atlantis.bioinfolab.sns.it/mcp';

// Natural-language prompt library, grouped. These are things a user types to
// the assistant in plain English once the connector is enabled — NOT tool syntax.
const promptGroups = [
  {
    title: 'Getting started',
    blurb:
      'Ask the assistant what it can do with Atlantis. It will discover the API by itself.',
    prompts: [
      'Using Atlantis, what kinds of questions can you answer about a protein?',
      'List the Pathfinder analyses available in Atlantis and what each one is for.',
      'What data does Atlantis have for PRKACA?',
    ],
  },
  {
    title: 'Exploring a protein',
    blurb:
      'Browse residues, annotations, structures, variants and ligands in plain language.',
    prompts: [
      'Give me an overview of GNAO1 from Atlantis.',
      'Which experimental structures and AlphaFold models does Atlantis have for LRRK2?',
      'Show the ClinVar variants on TP53 that are pathogenic, with their AlphaMissense class.',
      'What PTMs are annotated on residue P17612_10 and what is around it?',
      'List the ligands bound near the active site of GSTP1.',
      'Which residues of PRKACA fall in the protein kinase domain (PF00069)?',
    ],
  },
  {
    title: 'Contacts of a structure',
    blurb:
      'Compute 3D residue contacts for any PDB id or AlphaFold model, then dig in.',
    prompts: [
      'Calculate the contacts of 3AMA with a cutoff of 5 Å.',
      'Compute the 3D contacts for the AlphaFold model of GNAO1 and tell me the most connected residues.',
      'For PDB 2GU8, which residues line the ATP pocket?',
      'Compute contacts for 3AMA, then run a VUS structural prioritisation on that structure.',
    ],
  },
  {
    title: 'Pathfinder — mechanism & interpretation',
    blurb:
      'Ask the biological question; the assistant picks the right Pathfinder level and inputs.',
    prompts: [
      'Which residues hold the PRKACA fold together? (structural hubs)',
      'Find the 3D contact path between residues P17612_49 and P17612_205 on 3ama structure.',
      'Is the interface between two interacting proteins enriched for pathogenic variants?',
      'For PRKACA, are pathogenic variants clustered around PTM sites more than benign ones?',
      'Does the crystal structure of PRKACA differ from its AlphaFold model? Where are the hinges?',
      'I have two GNAO1 variants, R209H and E246K — triage them against everything Atlantis knows.',
      'My VUS is in a protein; is the same position pathogenic in a related family member?',
    ],
  },
  {
    title: 'From a clinical / genetics starting point',
    blurb:
      'Atlantis is indexed by protein and residue. The assistant can bridge from a gene or ' +
      'variant, but a genomic SNP id (rsID) usually needs another connector first.',
    prompts: [
      'For BRCA1 R1699Q, interpret the structural context using Atlantis.',
      'A patient carries PRKACA L206R — what does Atlantis say about that residue?',
      'I have a missense variant in GNAO1 at position 209 (R>H); is it near a functional site?',
    ],
  },
];

const MCPPage = () => {
  const [copied, setCopied] = useState('');

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <>
      <Helmet>
        <title> Atlantis | MCP</title>
      </Helmet>
      <Header />
      <section className="px-5 py-10">
        <div className="container">

          {/* Intro */}
          <div className="row mb-4">
            <div className="col-12">
              <h1 className="display-5 fw-bold mb-3">Remote MCP</h1>
              <p className="fs-1 text-justify">
                Atlantis is available as a Model Context Protocol (MCP) connector, so
                you can ask questions about the human proteome — residues, 3D contacts,
                ClinVar variants, PTMs, domains, and the full set of Pathfinder graph
                analyses — directly inside your AI assistant, in plain language. The assistant discovers
                the API on its own and builds the queries for you.
              </p>
              <p className="fs-1 text-justify">
                MCP is an open standard, so the same endpoint works across MCP-compatible
                assistants — not just one vendor. The examples below say “the assistant”;
                use whichever you prefer.
              </p>

              <h2 className="display-6 fw-bold mb-3">Connector URL</h2>
              <p className="fs-1 text-justify">
                Add this remote MCP server (Streamable HTTP) to any MCP-compatible client:
              </p>
              <Code className="fs-1 text-justify mb-3" children={MCP_URL} />
              <p className="fs-1 text-justify">
                It is open (no authentication) and mirrors the same data as our{' '}
                <a className="link-color" href={`${process.env.PUBLIC_URL}/api`}>
                  <i>GraphQL API</i>
                </a>. Because it is a remote HTTPS endpoint, it works both in chat apps
                (Claude, ChatGPT, Gemini) and in developer tools (Cursor, VS Code, Claude
                Code, Gemini CLI), as well as any custom app built on an MCP SDK.
              </p>

              <h2 className="display-6 fw-bold mb-3">How to connect</h2>
              <ul className="mb-1 ml-10">
                <li className="fs-1 text-justify">
                  <b>Claude (Pro / Max):</b> Settings → Connectors → <b>Add custom
                  connector</b> → paste the URL → <b>Add</b> (leave OAuth empty). On
                  Team / Enterprise an Owner adds it once, then members enable it.
                  Add it once on the web and it is available in the mobile app too.
                </li>
                <li className="fs-1 text-justify">
                  <b>ChatGPT:</b> add it as a custom connector / MCP server in settings.
                  ChatGPT accepts remote HTTPS endpoints (like this one) — which is
                  exactly the transport used here.
                </li>
                <li className="fs-1 text-justify">
                  <b>Biomni:</b> Settings → Connectors → <b>Add custom connector</b> →
                  paste the URL. The form requires an authentication method: pick{' '}
                  <b>Personal access token</b> and type <b>none</b> as the
                  token.
                </li>
                <li className="fs-1 text-justify">
                  <b>Gemini:</b> connect it via the Gemini CLI (or Gemini Enterprise) by
                  adding the URL to your MCP server configuration.
                </li>
                <li className="fs-1 text-justify">
                  <b>Cursor / VS Code / Claude Code / other dev tools:</b> add the URL to
                  the client’s <b>mcpServers</b> configuration as a
                  Streamable HTTP server.
                </li>
                <li className="fs-1 text-justify">
                  <b>Custom apps:</b> point any MCP client SDK (Python / TypeScript) at
                  the URL to use the tools programmatically with the model of your choice.
                </li>
              </ul>
              <p className="fs-1 text-justify">
                Exact menu names differ per client and change over time; check your
                assistant’s documentation for “custom connector” or “MCP server”.
              </p>

              <h2 className="display-6 fw-bold mb-3">What you can ask</h2>
              <p className="fs-1 text-justify">
                Enable the Atlantis connector in a conversation and type questions
                naturally. You don’t need to name tools or levels — the assistant chooses
                them. A UniProt accession (e.g. <b>P17612</b>), a gene symbol (e.g.{' '}
                <b>PRKACA</b>), a PDB id (e.g. <b>3AMA</b>) or a residue
                (<b>P17612_201</b>) are the usual entry points. Click any example to copy it.
              </p>
            </div>
          </div>

          {/* Prompt library */}
          {promptGroups.map((group) => (
            <div className="row mb-4" key={group.title}>
              <div className="col-12">
                <h2 className="display-6 fw-bold mb-2">{group.title}</h2>
                <p className="fs-1 text-justify">{group.blurb}</p>
                <div className="d-flex flex-column">
                  {group.prompts.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="btn btn-example mb-2 text-start"
                      onClick={() => copy(p)}
                      title="Click to copy"
                    >
                      “{p}” {copied === p ? <b> — copied</b> : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Notes */}
          <div className="row">
            <div className="col-12">
              <h2 className="display-6 fw-bold mb-3">Good to know</h2>
              <ul className="mb-1 ml-10">
                <li className="fs-1 text-justify">
                  <b>Gene or accession:</b> gene symbols are resolved to UniProt
                  accessions automatically, so “PRKACA” and “P17612” both work.
                </li>
                <li className="fs-1 text-justify">
                  <b>Structure needed:</b> for analyses that require a structure, if you
                  don’t give one an AlphaFold model is selected automatically.
                </li>
                <li className="fs-1 text-justify">
                  <b>Your own structure:</b> the assistant can compute contacts for any PDB id
                  or AlphaFold model on the fly (file upload is not supported over MCP).
                </li>
                <li className="fs-1 text-justify">
                  <b>SNP ids (rsID):</b> Atlantis is indexed by protein and residue.
                  A genomic rsID usually has to be translated to a gene and amino-acid
                  change first (the assistant can use other connectors for that), and only
                  protein-coding (missense) variants map to a residue.
                </li>
                <li className="fs-1 text-justify">
                  <b>Clients that force an auth choice:</b> the endpoint is open, but some
                  clients make the authentication field mandatory. In that case choose the
                  token option and enter any placeholder (e.g. <Code children="none" />)
                  rather than OAuth, which would trigger a dynamic client registration the
                  server does not implement.
                </li>
                <li className="fs-1 text-justify">
                  <b>Interpretation, not diagnosis:</b> results are exploratory evidence
                  (AlphaMissense, ClinVar, structure, evolution), not a clinical verdict.
                </li>
              </ul>
            </div>
          </div>

        </div>
      </section>
      <Footer />
    </>
  );
};

export default MCPPage;